<template>
  <div class="observable-scroll-box">
    <slot/>
  </div>
</template>

<script>
import simpleID from '../../../lib/simpleID'
export default {
  name: 'ObservableScrollBox',
  props: {
    observeSelector: { type: String, default: '' },
    observeMargin: { type: String, default: '0px 0px 0px 0px' },
    observeNumSteps: { type: Number, default: 0 },
    observeThreshold: { type: Array, default: null },

    sortFunc: {
      type: Function,
      default: (a, b) => {
        return b.intersectionRatio - a.intersectionRatio
      }
    }
  },

  data: () => ({
    inViewObserver: null,
    childrenObserver: null,
    reportStorage: null,
    observableElements: [],
    threshold: [0]
  }),

  watch: {
    observeSelector () {
      this.resetChildrenObserver()
      this.resetViewObserver()
    },
    observeMargin () {
      this.resetViewObserver()
    },
    observeThreshold (value) {
      this.threshold = value
      this.resetViewObserver()
    },
    observeNumSteps (value) {
      if (this.observeThreshold) return
      this.threshold = this.thresholdList(value)
      this.resetViewObserver()
    }
  },

  mounted () {
    this.threshold = this.observeThreshold || this.thresholdList(this.observeNumSteps)
    this.observableElements = this.getObservableElements()
    this.updateReportStorage()
    this.setViewObserver()
    this.setChildrenObserver()
  },

  destroyed () {
    this.disconnectViewObserver()
    this.disconnectChildrenObserver()
  },

  methods: {
    inViewReport (entries) {
      const report = entries.reduce((res, entry) => {
        const el = entry.target
        res[el.dataset.ioId] = {
          el,
          intersectionRect: entry.intersectionRect,
          intersectionRatio: entry.intersectionRatio
        }
        return res
      }, {})
      this.reportStorage = { ...this.reportStorage, ...report }
      this.$emit('in-view', Object.values(this.reportStorage).sort(this.sortFunc))
    },

    setViewObserver () {
      this.inViewObserver = new IntersectionObserver(this.inViewReport, {
        root: this.$el,
        rootMargin: this.observeMargin,
        threshold: this.observeThreshold
      })
      this.observableElements.forEach(el => {
        this.inViewObserver.observe(el)
      })
    },

    resetViewObserver () {
      this.disconnectViewObserver()
      this.setViewObserver()
    },

    disconnectViewObserver () {
      if (this.inViewObserver) this.inViewObserver.disconnect()
    },

    disconnectChildrenObserver () {
      if (this.childrenObserver) this.childrenObserver.disconnect()
    },

    /**
     * @return {HTMLElement[]}
     */
    getObservableElements () {
      const selector = this.observeSelector || ':scope > *'
      return Array.from(this.$el.querySelectorAll(selector)).map(/** @type HTMLElement */el => {
        if (!el.dataset.ioId) {
          el.dataset.ioId = el.id || simpleID()
        }
        return el
      })
    },

    updateReportStorage () {
      this.reportStorage = this.observableElements.reduce((res, el) => {
        const id = el.dataset.ioId
        res[id] = {
          el,
          intersectionRect: {},
          intersectionRatio: 0,
          ...((this.reportStorage || {})[id] || {})
        }
        return res
      }, {})
    },

    setChildrenObserver () {
      const makeSubTreeCallback = () => (mutationsList) => {
        const mutation = mutationsList.find(mutation => mutation.type === 'childList')
        if (!mutation) return

        // removed elements
        const removedNodes = Array.from(mutation.removedNodes)
        this.arrayIntersection(this.observableElements, removedNodes).forEach(el => {
          this.inViewObserver.unobserve(el)
        })

        this.observableElements = this.getObservableElements()
        this.updateReportStorage()

        // added elements
        const addedNodes = Array.from(mutation.addedNodes)
        this.arrayIntersection(this.observableElements, addedNodes).forEach(el => {
          this.inViewObserver.observe(el)
        })
      }

      const makeDirectChildrenCallback = () => (mutationsList) => {
        const mutation = mutationsList.find(mutation => mutation.type === 'childList')
        if (!mutation) return

        this.observableElements = this.getObservableElements()
        this.updateReportStorage()

        Array.from(mutation.removedNodes).forEach(el => {
          this.inViewObserver.unobserve(el)
        })

        Array.from(mutation.addedNodes).forEach(el => {
          this.inViewObserver.observe(el)
        })
      }

      const callback = this.observeSelector ? makeSubTreeCallback() : makeDirectChildrenCallback()
      this.childrenObserver = new MutationObserver(callback)
      this.childrenObserver.observe(this.$el, { childList: true, subtree: !!this.observeSelector })
    },

    resetChildrenObserver () {
      this.disconnectChildrenObserver()
      this.setChildrenObserver()
    },

    thresholdList (numSteps) {
      let thresholds = []
      if (!numSteps) numSteps = 1
      for (let i = 1.0; i <= numSteps; i++) {
        let ratio = i / numSteps
        thresholds.push(ratio)
      }
      thresholds.push(0)
      return thresholds
    },

    arrayIntersection (arr1, arr2) {
      return arr1.filter(value => arr2.includes(value))
    }
  }
}
</script>

<style lang="scss">
  .observable-scroll-box {
    overflow: auto;
  }
</style>
